import { useState, useEffect, useRef } from 'react';
import { supabase } from '../utils/supabaseClient';

const BUFFER_BEHIND = 5;  // Keep 5 images behind the playhead
const BUFFER_AHEAD = 30;  // Load 30 images ahead (approx 4 seconds at 8x speed)

/**
 * usePhotoBuffer
 * Implements a sliding window strategy to lazily load signed URLs for images.
 * prevents network saturation by only loading what is needed for playback.
 * * @param {Array} photos - Array of photo metadata objects (must contain 'storage_path' and 'filename')
 * @param {number} currentIndex - The current index of the playback head
 * @returns {Object} { cache } - A dictionary mapping filenames to signed URLs
 */
const usePhotoBuffer = (photos, currentIndex) => {
  const [cache, setCache] = useState({});
  
  // Track ongoing requests to prevent duplicate fetches for the same file
  const activeRequests = useRef(new Set());

  useEffect(() => {
    if (!photos || photos.length === 0) return;

    const loadBuffer = async () => {
      // 1. Calculate the sliding window range
      const start = Math.max(0, currentIndex - BUFFER_BEHIND);
      const end = Math.min(photos.length, currentIndex + BUFFER_AHEAD);
      
      // Get the slice of photos that SHOULD be in memory
      const windowPhotos = photos.slice(start, end);

      // 2. Identify missing photos (in window but not in cache)
      const missingPhotos = windowPhotos.filter(p => {
        // Check if we have it in cache OR if we are currently fetching it
        return !cache[p.filename] && !activeRequests.current.has(p.filename);
      });

      if (missingPhotos.length === 0) return;

      // 3. Mark these as "fetching" immediately to prevent re-entry
      missingPhotos.forEach(p => activeRequests.current.add(p.filename));

      // 4. Batch fetch signed URLs
      // Supabase createSignedUrls (plural) allows signing multiple paths in one HTTP request
      // This is much more efficient than looping single requests.
      const paths = missingPhotos.map(p => p.storage_path);
      
      try {
        const { data, error } = await supabase
          .storage
          .from('session-files')
          .createSignedUrls(paths, 3600); // 1 hour expiry

        if (error) throw error;

        if (data) {
          // 5. Create Image objects to force browser preload
          const newCacheEntries = {};
          
          const preloadPromises = data.map((item) => {
            // Match the signed URL back to the filename using the path
            // Note: Supabase returns { path, signedUrl }
            const originalPhoto = missingPhotos.find(p => p.storage_path === item.path);
            
            if (originalPhoto && item.signedUrl) {
              newCacheEntries[originalPhoto.filename] = item.signedUrl;
              
              // Create a detached Image object. 
              // Setting .src forces the browser to download and decode it immediately.
              return new Promise((resolve) => {
                const img = new Image();
                img.onload = resolve;
                img.onerror = resolve; // Resolve even on error to not block Promise.all
                img.src = item.signedUrl;
              });
            }
            return Promise.resolve();
          });

          // Wait for preloading to finish (optional, but ensures smoothness)
          await Promise.all(preloadPromises);

          // 6. Update State
          setCache(prev => ({
            ...prev,
            ...newCacheEntries
          }));
        }
      } catch (err) {
        console.error('Error buffering photos:', err);
      } finally {
        // Clear active flags
        missingPhotos.forEach(p => activeRequests.current.delete(p.filename));
      }
    };

    // 7. Prune Cache (Optional but recommended for very long sessions)
    // Remove images that are far behind the playhead (e.g., > 50 frames back)
    const pruneCache = () => {
      setCache(prev => {
        const newCache = { ...prev };
        let hasChanges = false;
        
        // Threshold for keeping images in memory
        const PRUNE_THRESHOLD = 50; 
        
        Object.keys(newCache).forEach(filename => {
           // Find the index of this file
           const photoIndex = photos.findIndex(p => p.filename === filename);
           
           // If it's valid but very old (behind current index by > threshold)
           if (photoIndex !== -1 && photoIndex < currentIndex - PRUNE_THRESHOLD) {
             delete newCache[filename];
             hasChanges = true;
           }
        });

        return hasChanges ? newCache : prev;
      });
    };

    loadBuffer();
    
    // Only prune occasionally (e.g., every 50 frames) to save CPU
    if (currentIndex % 50 === 0) {
      pruneCache();
    }

  }, [currentIndex, photos]); // Re-run when playhead moves

  return { cache };
};

export default usePhotoBuffer;